import { useEffect, useLayoutEffect, useRef } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog";
import { Spinner } from "../../../components/ui/spinner";
import type { MCPConnection } from "../types";
import { useTranslation } from "react-i18next";

type Props = {
  connection: MCPConnection | null;
  isDeleting: boolean;
  onCancel: () => void;
  onConfirm: () => void | Promise<unknown>;
  focusFallback?: () => HTMLElement | null;
};

const DeleteConnectionDialog = ({
  connection,
  isDeleting,
  onCancel,
  onConfirm,
  focusFallback,
}: Props) => {
  const { t } = useTranslation();
  const open = connection !== null;
  const operationRef = useRef<object | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const wasOpen = useRef(false);
  const openingPath = useRef(window.location.pathname);
  useEffect(() => {
    if (!open || !isDeleting) operationRef.current = null;
  }, [open, isDeleting]);
  useLayoutEffect(() => {
    if (open && !wasOpen.current) {
      openingPath.current = window.location.pathname;
      openerRef.current = document.activeElement instanceof HTMLElement && document.activeElement !== document.body
        ? document.activeElement : null;
    }
    wasOpen.current = open;
  }, [open]);
  const eligible = (element: HTMLElement | null): element is HTMLElement => {
    if (!element || element === document.body || !element.isConnected ||
      element.matches(":disabled, [aria-disabled='true']")) return false;
    for (let node: HTMLElement | null = element; node; node = node.parentElement) {
      const style = getComputedStyle(node);
      if (node.matches("[hidden], [inert], [aria-hidden='true']") ||
        style.display === "none" || style.visibility === "hidden" || style.visibility === "collapse") return false;
    }
    return true;
  };

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !isDeleting && !operationRef.current) onCancel();
      }}
    >
      <AlertDialogContent className="border-border bg-card"
        onEscapeKeyDown={(event) => {
          if (isDeleting || operationRef.current) event.preventDefault();
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          if (openingPath.current !== window.location.pathname) return;
          const opener = openerRef.current;
          const target = eligible(opener) ? opener : focusFallback?.() ?? null;
          if (eligible(target)) {
            target.focus();
          }
          openerRef.current = null;
        }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle className="text-foreground">
            {t("mcpPage.deleteDialog.title")}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {connection
              ? t("mcpPage.deleteDialog.description", { name: connection.name })
              : ""}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            className="border-border bg-card text-foreground hover:bg-accent"
            disabled={isDeleting}
          >
            {t("mcpPage.actions.cancel")}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(event) => {
              event.preventDefault();
              if (isDeleting || operationRef.current) return;
              const operation = {};
              operationRef.current = operation;
              try {
                const result = onConfirm();
                if (result) void Promise.resolve(result).catch(() => {
                  // The mutation hook presents the error; retain the dialog.
                }).finally(() => {
                  if (operationRef.current === operation) operationRef.current = null;
                });
              } catch (error) {
                operationRef.current = null;
                throw error;
              }
            }}
            disabled={isDeleting}
            aria-busy={isDeleting}
            className="bg-destructive text-white hover:bg-destructive/90"
          >
            {isDeleting ? (
              <>
                <Spinner className="size-4" />
                <span>{t("mcpPage.actions.deleting")}</span>
              </>
            ) : (
              t("mcpPage.actions.delete")
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DeleteConnectionDialog;
